import Phaser from 'phaser';

/**
 * InputManager: Unified input handling for keyboard, touch, and gamepad.
 * Provides a consistent interface regardless of input device.
 */
export interface InputState {
  left: boolean;
  right: boolean;
  fire: boolean;
  pause: boolean;
}

export class InputManager {
  private scene: Phaser.Scene;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private fireKey!: Phaser.Input.Keyboard.Key;
  private aKey!: Phaser.Input.Keyboard.Key;
  private dKey!: Phaser.Input.Keyboard.Key;
  private touchState: InputState = {
    left: false,
    right: false,
    fire: false,
    pause: false,
  };

  // Gamepad state tracking
  private gamepadConnected = false;
  private lastGamepadPauseState = false; // For edge detection on pause button

  // Analog stick deadzone threshold
  private readonly STICK_DEADZONE = 0.3;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    // Keyboard setup
    if (this.scene.input.keyboard) {
      this.cursors = this.scene.input.keyboard.createCursorKeys();
      this.fireKey = this.scene.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE
      );
      // WASD keys for additional movement support
      this.aKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.dKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    }

    // Gamepad connection/disconnection detection
    if (this.scene.input.gamepad) {
      this.scene.input.gamepad.on('connected', (pad: Phaser.Input.Gamepad.Gamepad) => {
        this.gamepadConnected = true;
        console.log(`Gamepad connected: ${pad.id}`);
      });

      this.scene.input.gamepad.on('disconnected', (pad: Phaser.Input.Gamepad.Gamepad) => {
        this.gamepadConnected = false;
        console.log(`Gamepad disconnected: ${pad.id}`);
      });

      // Check if a gamepad is already connected
      if (this.scene.input.gamepad.gamepads.length > 0) {
        this.gamepadConnected = true;
      }
    }
  }

  getState(): InputState {
    const keyboard = this.getKeyboardState();
    const gamepad = this.getGamepadState();
    const touch = this.touchState;

    return {
      left: keyboard.left || gamepad.left || touch.left,
      right: keyboard.right || gamepad.right || touch.right,
      fire: keyboard.fire || gamepad.fire || touch.fire,
      pause: keyboard.pause || gamepad.pause || touch.pause,
    };
  }

  private getKeyboardState(): InputState {
    if (!this.cursors) {
      return { left: false, right: false, fire: false, pause: false };
    }
    return {
      left: this.cursors.left.isDown || (this.aKey?.isDown ?? false),
      right: this.cursors.right.isDown || (this.dKey?.isDown ?? false),
      fire: this.fireKey?.isDown || false,
      pause: false, // Handled via key events in GameScene
    };
  }

  private getGamepadState(): InputState {
    const pads = this.scene.input.gamepad?.gamepads;
    if (!pads || pads.length === 0) {
      return { left: false, right: false, fire: false, pause: false };
    }

    const pad = pads[0];
    if (!pad) {
      return { left: false, right: false, fire: false, pause: false };
    }

    // D-pad and left stick for movement (with deadzone)
    const leftStick = pad.leftStick;
    const stickLeft = leftStick ? leftStick.x < -this.STICK_DEADZONE : false;
    const stickRight = leftStick ? leftStick.x > this.STICK_DEADZONE : false;

    // Fire: A button (index 0), X button (index 2), or right trigger
    const firePressed = pad.A || pad.X || this.isButtonPressed(pad, 7); // R2/Right trigger

    // Pause: Start button (index 9) - with edge detection to prevent repeated toggling
    const startButtonDown = this.isButtonPressed(pad, 9);
    const pauseEdge = startButtonDown && !this.lastGamepadPauseState;
    this.lastGamepadPauseState = startButtonDown;

    return {
      left: pad.left || stickLeft,
      right: pad.right || stickRight,
      fire: firePressed,
      pause: pauseEdge,
    };
  }

  /**
   * Check if a specific gamepad button by index is pressed.
   * Safe check handles missing buttons gracefully.
   */
  private isButtonPressed(pad: Phaser.Input.Gamepad.Gamepad, buttonIndex: number): boolean {
    try {
      const button = pad.buttons[buttonIndex];
      return button ? button.pressed : false;
    } catch {
      return false;
    }
  }

  /**
   * Check if a gamepad is currently connected
   */
  isGamepadConnected(): boolean {
    return this.gamepadConnected;
  }

  /**
   * Set touch control state (called by virtual button handlers).
   */
  setTouchState(key: keyof InputState, value: boolean): void {
    this.touchState[key] = value;
  }

  destroy(): void {
    // Cleanup gamepad event listeners
    if (this.scene.input.gamepad) {
      this.scene.input.gamepad.removeAllListeners();
    }
  }
}
